/**
 * The person's side of the OverpoweredApps help desk (2026-10-05):
 * My reports, replies, deleting a report, and the one suite-wide setting.
 *
 * Shapes are OPApps' (`OPApps/CONTRACTS/op-report.md` section 3), and the
 * words shown to the person - status labels, the automatic "received", the
 * operator's replies - come from the server as they are, never re-worded here,
 * so every OP app says the same thing about the same report.
 *
 * The reply badge polls `?app=opbuttons` rarely (every 10 minutes, and when
 * the app comes back to the foreground), never while the suite is performing,
 * and only while signed in. A badge is a courtesy; it must not cost a gig.
 */

import { createSignal, createRoot, createEffect, on, type Accessor } from 'solid-js';
import { opid, authStatus } from '../auth/opid.ts';
import { performanceMode } from '../opbridge/performanceMode.ts';
import { watchJob } from './watchdog.ts';

export const HELP_DESK = 'https://overpoweredapps.net';
const POLL_MS = 10 * 60_000;

export interface MyReportRow {
  id: string;
  app: string;
  app_name?: string;
  type: string;
  title: string;
  status: string;
  status_label: string;
  fixed_in?: string | null;
  build?: string;
  received_at: string;
  updated_at: string;
  unread: boolean;
}

/** `status_label` (op-report.md, 2026-10-06): the server's words for the status
 *  this message set, in the report's TYPE (an answered question is "Answered",
 *  never "Fixed"); null on a plain message. Shown as it is, never mapped from
 *  `status` here. */
export interface MyReportMessage { at: string; from: 'you' | 'op'; kind: string; status?: string | null; status_label?: string | null; body: string }

export interface MyReportDetail {
  report: Record<string, unknown> & { id: string; title?: string; status?: string; status_label?: string; note?: string };
  messages: MyReportMessage[];
  attachments: { name: string; content_type: string; bytes: number }[];
}

export type DeskResult<T> = { ok: true; value: T } | { ok: false; reason: 'signin' | 'offline' | 'error'; status?: number };

/* Same rule and seam as the outbox: no token is asked for unless signed in,
   and a sealed smoke stands in for the suite broker. */
let tokenSource: () => Promise<string | null> = () => (authStatus() === 'signed-in' ? opid.getToken() : Promise.resolve(null));
export function setHelpDeskTokenSourceForTests(fn: () => Promise<string | null>): void { tokenSource = fn; }

async function call<T>(method: string, path: string, body?: unknown): Promise<DeskResult<T>> {
  const token = await tokenSource().catch(() => null);
  if (!token) return { ok: false, reason: 'signin' };
  let res: Response;
  try {
    res = await fetch(`${HELP_DESK}${path}`, {
      method,
      headers: { Authorization: `Bearer ${token}`, ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}) },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      credentials: 'omit',
    });
  } catch {
    return { ok: false, reason: 'offline' };
  }
  if (res.status === 401) return { ok: false, reason: 'signin', status: 401 };
  if (!res.ok) return { ok: false, reason: 'error', status: res.status };
  const value = (await res.json().catch(() => ({}))) as T;
  return { ok: true, value };
}

const [unread, setUnread] = createSignal(0);
/** Replies or status changes on OPButtons reports the person has not opened. */
export const myReportsUnread: Accessor<number> = unread;

export async function listMyReports(): Promise<DeskResult<{ reports: MyReportRow[]; unread: number }>> {
  const r = await call<{ reports?: MyReportRow[]; unread?: number }>('GET', '/api/reports/mine?app=opbuttons');
  if (!r.ok) return r;
  const value = { reports: r.value.reports ?? [], unread: r.value.unread ?? 0 };
  setUnread(value.unread);
  return { ok: true, value };
}

/** Opening a report clears its unread mark on the server. */
export async function openMyReport(id: string): Promise<DeskResult<MyReportDetail>> {
  const r = await call<MyReportDetail>('GET', `/api/reports/mine/${encodeURIComponent(id)}`);
  if (r.ok) void refreshUnread();
  return r;
}

export function replyToMyReport(id: string, reply: string): Promise<DeskResult<unknown>> {
  return call('POST', `/api/reports/mine/${encodeURIComponent(id)}`, { reply: reply.slice(0, 4000) });
}

export function deleteMyReport(id: string): Promise<DeskResult<unknown>> {
  return call('DELETE', `/api/reports/mine/${encodeURIComponent(id)}`);
}

/** The ONE suite-wide switch: send problem reports without asking. */
export async function readAutoSend(): Promise<DeskResult<boolean>> {
  const r = await call<{ reports_auto_send?: boolean }>('GET', '/api/settings');
  return r.ok ? { ok: true, value: r.value.reports_auto_send === true } : r;
}

export async function writeAutoSend(on: boolean): Promise<DeskResult<boolean>> {
  const r = await call<{ reports_auto_send?: boolean }>('POST', '/api/settings', { reports_auto_send: on });
  return r.ok ? { ok: true, value: r.value.reports_auto_send === true } : r;
}

async function refreshUnread(): Promise<void> {
  watchJob('helpdesk-unread');
  if (performanceMode() || authStatus() !== 'signed-in') return;
  lastCheck = Date.now();
  await listMyReports();
}

/* Checked when the dot could be looked at, not only on a timer (2026-10-05:
   the live test's "looking" never showed a dot - the app had checked at boot
   and its next look was ten minutes away, and on a desktop switching windows
   does not hide the tab). At most once per CHECK_GAP_MS. */
const CHECK_GAP_MS = 30_000;
let lastCheck = 0;
/** Re-check the reply count now, unless it was checked in the last 30 s. */
export function refreshUnreadSoon(): void {
  if (Date.now() - lastCheck < CHECK_GAP_MS) return;
  void refreshUnread();
}

let installed = false;
/** Start the badge poll. Idempotent; called once from App. */
export function installHelpDeskBadge(): void {
  if (installed) return;
  installed = true;
  setInterval(() => void refreshUnread(), POLL_MS);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') refreshUnreadSoon(); });
  window.addEventListener('focus', () => refreshUnreadSoon());
  createRoot(() => {
    createEffect(on(authStatus, (s) => { if (s === 'signed-in') void refreshUnread(); }));
  });
}
